import { useEffect, useRef } from "react";

// Autosaving draft persistence for Driver Mode request forms.
// - Autosaves (debounced) as the form changes — no manual "Save draft".
// - Survives app/browser backgrounding and navigation away/back.
// - Scoped to a caller-provided key (driver + vehicle + request type).
// - Restores once on mount; expires after EXPIRY_MS.
// - Caller clears the draft on successful submit or explicit discard via clearDraft().

const PREFIX = "relay_draft_";
const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24h

const keyFor = (scope) => `${PREFIX}${scope}`;

export function clearDraft(scope) {
  if (!scope) return;
  try { localStorage.removeItem(keyFor(scope)); } catch (_) { /* ignore */ }
}

export function useDraftPersistence({ scope, form, setForm, enabled = true, onRestore }) {
  const restored = useRef(false);

  // Restore once when a scope becomes available.
  useEffect(() => {
    if (!enabled || !scope || restored.current) return;
    restored.current = true;
    try {
      const raw = localStorage.getItem(keyFor(scope));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.savedAt && parsed.data && Date.now() - parsed.savedAt < EXPIRY_MS) {
        setForm((f) => ({ ...f, ...parsed.data }));
        if (onRestore) onRestore();
      } else {
        localStorage.removeItem(keyFor(scope));
      }
    } catch (_) { /* ignore corrupt draft */ }
  }, [scope, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced autosave on change (also fires before backgrounding).
  useEffect(() => {
    if (!enabled || !scope) return;
    const save = () => {
      try { localStorage.setItem(keyFor(scope), JSON.stringify({ savedAt: Date.now(), data: form })); } catch (_) { /* ignore */ }
    };
    const id = setTimeout(save, 400);
    const onHide = () => { if (document.visibilityState === "hidden") save(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearTimeout(id); document.removeEventListener("visibilitychange", onHide); };
  }, [scope, form, enabled]);
}
